import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Tooltip,
  Input,
} from "@heroui/react";
import {
  RentalContract,
  MilestoneDateType,
  ContactType,
} from "shared/types/rental-contracts";
import {
  calculateIndexation,
  buildIndexationRequest,
} from "./services/indexationApi";
import { IndexationResult } from "./types/indexation.types";
import { formatDate } from "shared/utils/get-dates";
import { applyMask } from "shared/utils/masks";
import {
  CheckCircle,
  TrendUp,
  Calculator,
  ArrowLeft,
  Globe,
  Envelope,
  Buildings,
  UserCircle,
  Calendar,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import ButtonNew from "components/buttonNew/buttonNew";
import { updateContractInCache } from "./hooks/indexationCache";
import { formatCorrectionFactor } from "./utils/indexationUtils";
import { deriveCurrentPrice } from "./utils/deriveProductPrices";
import { MassMailModal } from "screens/mass-mail";
import type { MassMailState } from "screens/mass-mail/types";

export interface IndexationCalculationPageProps {
  /** When rendered in a modal the contract + id are passed as props instead of
   *  being read from the route/localStorage. */
  contractId?: number;
  contract?: RentalContract;
  /** Renders in a modal-friendly layout (no sticky/full-page chrome). */
  embedded?: boolean;
  /** Called instead of navigating back to the overview (Sluiten / errors). */
  onClose?: () => void;
  /** Called after an indexation is successfully applied. */
  onApplied?: (contractId: number) => void;
}

export const IndexationCalculationPage = ({
  contractId: contractIdProp,
  contract: contractProp,
  embedded = false,
  onClose,
  onApplied,
}: IndexationCalculationPageProps = {}) => {
  const { contractId: routeContractId } = useParams<{ contractId: string }>();
  const contractId =
    contractIdProp != null ? String(contractIdProp) : routeContractId;
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useSession();

  // Close handler: use the callback when embedded, otherwise navigate back.
  const close = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    navigate("/rental/indexaties");
  }, [onClose, navigate]);

  // Email popup state — replaces navigating to /mass-mail in a (new) tab.
  const [emailModalState, setEmailModalState] =
    useState<Partial<MassMailState> | null>(null);

  // Try to get contract from props (modal) or navigation state first
  const contractFromState =
    contractProp ?? (location.state?.contract as RentalContract | undefined);

  const [contract, setContract] = useState<RentalContract | null>(null);
  const [isLoadingContract, setIsLoadingContract] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [result, setResult] = useState<IndexationResult | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [isApplied, setIsApplied] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);

  // Retroactive invoice state - split into previous months and current month
  const [previousMonthsMultiplier, setpreviousMonthsMultiplier] =
    useState<number>(0); // Max 3
  const [currentMonthMultiplier, setCurrentMonthMultiplier] =
    useState<number>(0); // Max 1 (0 or 1)
  const [retroactiveAmount, setRetroactiveAmount] = useState<number>(0);
  const [createInvoice, setCreateInvoice] = useState<boolean>(false);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);

  // Store original price before applying indexation (so we have correct oldPrice for email)
  const [originalActivePrice, setOriginalActivePrice] = useState<number | null>(
    null,
  );

  // Store backend response data
  const [backendIban, setBackendIban] = useState<string | null>(null);

  // Editable values - allow manual adjustment of calculated results
  const [editableIndexedRent, setEditableIndexedRent] = useState<string>("");

  // Attribute indexation tracking
  const [indexedAttributes, setIndexedAttributes] = useState<Set<number>>(
    new Set(),
  );
  const [attributeIndexedPrices, setAttributeIndexedPrices] = useState<
    Map<number, number>
  >(new Map());

  // Load contract data - prioritize state, then sessionStorage, fallback to API
  useEffect(() => {
    const loadContract = async () => {
      // Priority 1: Use contract from navigation state (passed from table)
      if (contractFromState) {
        console.log(
          "✅ [IndexationCalculationPage] Using contract from navigation state:",
          {
            id: contractFromState.id,
            initPrice: contractFromState.initPrice,
            activePrice: contractFromState.activePrice,
            hasEPC: !!contractFromState.currentEPC,
            hasMilestones: !!contractFromState.contractMilestoneDates,
          },
        );
        setContract(contractFromState);
        setIsLoadingContract(false);
        return;
      }

      // Priority 2: localStorage handoff (used when Bereken opens in a new tab).
      // The opener writes the full contract (with all relations); we read it,
      // validate freshness, and clear the key so it can't go stale.
      if (contractId) {
        const storageKey = `indexation-calculation-contract-${contractId}`;
        try {
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const { contract: parsed, savedAt } = JSON.parse(stored) as {
              contract: RentalContract;
              savedAt: number;
            };
            localStorage.removeItem(storageKey);
            const ageMs = Date.now() - (savedAt ?? 0);
            // Reject handoffs older than 60s — these are almost certainly stale
            // leftovers, and re-fetching from the API is safer.
            if (parsed && ageMs < 60_000) {
              console.log(
                "✅ [IndexationCalculationPage] Using contract from localStorage handoff",
              );
              setContract(parsed);
              setIsLoadingContract(false);
              return;
            }
          }
        } catch (error) {
          console.warn(
            "[IndexationCalculationPage] Kon contract niet uit localStorage lezen, valt terug op API fetch:",
            error,
          );
        }
      }

      // Priority 3: Fallback to API fetch (for direct URL access, page refresh, etc.)
      if (!contractId || !session) {
        setIsLoadingContract(false);
        return;
      }

      console.log(
        "⚠️ [IndexationCalculationPage] No contract in state, fetching from API...",
      );
      setIsLoadingContract(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/contracts/contract/${contractId}/for-indexation`,
          { method: "GET" },
          session,
        );

        if (response.ok) {
          const data = await response.json();
          console.log(
            "📊 [IndexationCalculationPage] Fetched contract from API:",
            {
              id: data.id,
              initPrice: data.initPrice,
              activePrice: data.activePrice,
              hasEPC: !!data.currentEPC,
              hasMilestones: !!data.contractMilestoneDates,
            },
          );
          setContract(data);
        } else {
          throw new Error("Failed to fetch contract");
        }
      } catch (error) {
        console.error(
          "❌ [IndexationCalculationPage] Error fetching contract:",
          error,
        );
        toast.error("Fout bij ophalen van contract");
        close();
      } finally {
        setIsLoadingContract(false);
      }
    };

    loadContract();
  }, [contractId, session, close, contractFromState]);

  // Calculate indexation function
  const handleCalculate = useCallback(async () => {
    if (!contract) return;

    // Validate required fields
    const signDate = contract.contractMilestoneDates?.find(
      (m) => m.milestoneDateType === MilestoneDateType.SIGN_DATE,
    )?.date;
    const startDate = contract.contractMilestoneDates?.find(
      (m) => m.milestoneDateType === MilestoneDateType.START_DATE,
    )?.date;

    const missing: string[] = [];
    if (!contract.initPrice || contract.initPrice <= 0)
      missing.push("basishuurprijs");
    if (!signDate) missing.push("ondertekeningsdatum");
    if (!startDate) missing.push("startdatum");
    if (!contract.currentEPC?.label) missing.push("EPC-label");

    if (missing.length > 0) {
      const message = `Indexatie kan niet berekend worden — ontbrekende gegevens: ${missing.join(", ")}. Open dit contract via de indexaties-overzichtspagina of vul de ontbrekende gegevens aan onder het tabje Verhuur van het gebouw.`;
      console.error(
        "[IndexationCalculationPage] Validation failed for indexation:",
        { contractId: contract.id, missing },
      );
      setCalculationError(message);
      return;
    }

    setIsCalculating(true);
    setCalculationError(null);
    try {
      const request = buildIndexationRequest(
        contract.initPrice!,
        signDate!,
        startDate!,
        contract.currentEPC!.label,
        contract.lastIndexationDate,
      );

      const calculationResult = await calculateIndexation(request, session);
      setResult(calculationResult);
      toast.success("Indexatie berekend met interne calculator");
    } catch (error) {
      console.error("Error calculating indexation:", error);
      const message =
        error instanceof Error
          ? error.message.replace(/^Indexation calculation error:\s*/i, "")
          : "Onbekende fout";
      setCalculationError(message);
      toast.error("Fout bij berekenen van indexatie");
    } finally {
      setIsCalculating(false);
    }
  }, [contract]);

  // Auto-calculate indexation when contract is loaded (skip confirmation step)
  useEffect(() => {
    if (!contract || result || isCalculating || calculationError) return;
    handleCalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, result, isCalculating, calculationError]);

  // Update editable indexed rent when result changes
  useEffect(() => {
    if (result) {
      setEditableIndexedRent(result.result.toFixed(2));
    }
  }, [result]);

  // Initialize indexed attributes when result changes - auto-check all non-zero attributes
  useEffect(() => {
    if (result && contract?.products) {
      const newIndexedAttributes = new Set<number>();
      const newAttributePrices = new Map<number, number>();

      contract.products.forEach((product) => {
        if (product.buildingAttribute) {
          const currentPrice = getCurrentProductPrice(product);
          if (currentPrice > 0) {
            // Auto-check non-zero attributes
            newIndexedAttributes.add(product.id);
            const indexedPrice = calculateIndexedAttributePrice(currentPrice);
            newAttributePrices.set(product.id, indexedPrice);
          }
        }
      });

      setIndexedAttributes(newIndexedAttributes);
      setAttributeIndexedPrices(newAttributePrices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, contract]);

  // Calculate retroactive months and amount when result changes
  useEffect(() => {
    if (!result || !contract) {
      setpreviousMonthsMultiplier(0);
      setCurrentMonthMultiplier(0);
      setRetroactiveAmount(0);
      return;
    }

    const anniversaryDate = contract.contractMilestoneDates?.find(
      (m) => m.milestoneDateType === MilestoneDateType.START_DATE,
    )?.date;

    if (!anniversaryDate) {
      setpreviousMonthsMultiplier(0);
      setCurrentMonthMultiplier(0);
      setRetroactiveAmount(0);
      return;
    }

    const now = new Date();
    const anniversary = new Date(anniversaryDate);

    // Build the correct anniversary date with the right year
    // (accounts for missed indexations where anniversary hasn't happened yet this year)
    const lastIdx = contract.lastIndexationDate;
    const hasRecentIdx =
      lastIdx &&
      now.getTime() - new Date(lastIdx).getTime() < 365 * 24 * 60 * 60 * 1000;
    const currentYearAnniv = new Date(
      now.getFullYear(),
      anniversary.getMonth(),
      anniversary.getDate(),
    );
    const annivYear =
      currentYearAnniv > now && !hasRecentIdx
        ? now.getFullYear() - 1
        : now.getFullYear();
    const correctAnniv = new Date(
      annivYear,
      anniversary.getMonth(),
      anniversary.getDate(),
    );

    // Calculate month difference using full date (year + month)
    let monthsDiff =
      (now.getFullYear() - correctAnniv.getFullYear()) * 12 +
      (now.getMonth() - correctAnniv.getMonth());
    if (monthsDiff < 0) monthsDiff = 0;

    const priceDiff =
      result.result -
      ((originalActivePrice ?? contract.activePrice?.price) || 0);

    // Previous months: Max 3, only if price difference > 0
    // These are the COMPLETED months since the anniversary
    const previousMonths =
      priceDiff > 0 ? Math.max(0, Math.min(monthsDiff, 3)) : 0;

    // Current month: 1 whenever the anniversary has already happened (we're in
    // the anniversary month or later) AND there's a price increase to compensate.
    // The previous `monthsDiff > 0` guard incorrectly dropped the current-month
    // compensation in the same calendar month as the anniversary, hiding the
    // retroactive section entirely when only the current month needed correction.
    // This is NOT manually adjustable — always compensate for current month.
    // Maximum: 3 previous + 1 current = 4 total months allowed.
    const currentMonth = priceDiff > 0 && correctAnniv <= now ? 1 : 0;

    const totalAmount = priceDiff * (previousMonths + currentMonth);

    console.log("🔄 [Auto-calculation] Retroactive compensation:", {
      monthsDiff,
      priceDiff: priceDiff.toFixed(2),
      previousMonths,
      currentMonth,
      totalMonths: previousMonths + currentMonth,
      totalAmount: totalAmount.toFixed(2),
    });

    setpreviousMonthsMultiplier(previousMonths);
    setCurrentMonthMultiplier(currentMonth);
    setRetroactiveAmount(Math.max(0, totalAmount));

    // Auto-enable invoice creation if amount > 0
    setCreateInvoice(totalAmount > 0);
  }, [result, contract, originalActivePrice]);

  const handleApply = async () => {
    if (!result || !contract) return;

    // Store original active price BEFORE applying indexation
    setOriginalActivePrice(contract.activePrice?.price || 0);

    // Calculate values from editable indexed rent
    const finalIndexedRent = parseFloat(editableIndexedRent) || result.result;
    const priceDiff =
      finalIndexedRent -
      (originalActivePrice || contract.activePrice?.price || 0);
    const totalMonths = previousMonthsMultiplier + currentMonthMultiplier;
    const finalCompensationAmount = createInvoice ? priceDiff * totalMonths : 0;

    // Build array of indexed products
    const indexedProducts: Array<{
      productId: number;
      newPrice: number;
      isRentalProduct?: boolean;
      isAttribute?: boolean;
    }> = [];

    // Find the rental product (ledger 700001 or 700002)
    const rentalProduct = contract.products?.find(
      (p) => p.template?.ledger === 700001 || p.template?.ledger === 700002,
    );

    if (rentalProduct) {
      indexedProducts.push({
        productId: rentalProduct.id,
        newPrice: finalIndexedRent,
        isRentalProduct: true,
      });
    }

    // Add only checked building attributes
    contract.products?.forEach((product) => {
      if (product.buildingAttribute) {
        const currentPrice = getCurrentProductPrice(product);
        if (currentPrice > 0 && indexedAttributes.has(product.id)) {
          // Only include checked attributes
          const indexedPrice =
            attributeIndexedPrices.get(product.id) ||
            calculateIndexedAttributePrice(currentPrice);
          indexedProducts.push({
            productId: product.id,
            newPrice: indexedPrice,
            isAttribute: true,
          });
        }
      }
    });

    setIsApplying(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/indexation/apply`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contractId: contract.id,
            indexedProducts: indexedProducts, // Array of products to index
            indexationDate: new Date().toISOString(),
            monthsMultiplier: createInvoice ? totalMonths : 0, // Total months (previous + current)
            compensationAmount: finalCompensationAmount, // Auto-calculated compensation amount
            calculationDetails: {
              base: result.base,
              indexOld: result.indexOld,
              indexNew: result.index,
              correctionCoefficient: result.correctionCoefficient,
            },
          }),
        },
        session,
      );

      if (response.ok) {
        const responseData = await response.json();
        toast.success(
          "Indexatie toegepast! Actieve prijs aangepast en gepusht naar Odoo.",
        );
        setIsApplied(true);

        // Notify the overview so it can drop this row from the list without a
        // full reload (keeps the user's scroll position).
        if (contract.id != null) onApplied?.(contract.id);

        // Store invoice data if created
        if (responseData.invoiceId) {
          // Use URL from backend (already includes correct Odoo environment)
          setInvoiceUrl(responseData.invoiceUrl);
          // Store backend response data for email
          setBackendIban(responseData.iban);
        }

        // Update contract state with new active price (use editable value)
        setContract((prev) =>
          prev
            ? {
                ...prev,
                activePrice: {
                  price: finalIndexedRent,
                  odooProductId: prev.activePrice?.odooProductId || 0,
                  equalsInOdoo: prev.activePrice?.equalsInOdoo || false,
                },
              }
            : null,
        );

        // Update the contract in the indexation overview cache so the row
        // reflects the new data when the user navigates back
        if (contract.id != null)
          updateContractInCache(contract.id, {
            lastIndexationDate: new Date() as any,
            activePrice: {
              price: finalIndexedRent,
              odooProductId: contract.activePrice?.odooProductId || 0,
              equalsInOdoo: contract.activePrice?.equalsInOdoo || false,
            } as any,
          });
      } else {
        throw new Error("Failed to apply indexation");
      }
    } catch (error) {
      console.error("Error applying indexation:", error);
      toast.error("Fout bij toepassen van indexatie");
    } finally {
      setIsApplying(false);
    }
  };

  const handleOpenOfficialTool = () => {
    window.open(
      "https://statbel.fgov.be/nl/themas/consumptieprijsindex/huurcalculator",
      "_blank",
    );
  };

  // Calculate indexed price for an attribute using the same formula as rental products
  const calculateIndexedAttributePrice = useCallback(
    (basePrice: number): number => {
      if (!result) return basePrice;
      // Formula: basePrice × (indexNew / indexOld) × correctionCoefficient
      return (
        basePrice *
        (result.index / result.indexOld) *
        result.correctionCoefficient
      );
    },
    [result],
  );

  // Toggle attribute indexation
  const toggleAttributeIndexation = useCallback(
    (productId: number, basePrice: number) => {
      setIndexedAttributes((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(productId)) {
          newSet.delete(productId);
          setAttributeIndexedPrices((prevPrices) => {
            const newPrices = new Map(prevPrices);
            newPrices.delete(productId);
            return newPrices;
          });
        } else {
          newSet.add(productId);
          const indexedPrice = calculateIndexedAttributePrice(basePrice);
          setAttributeIndexedPrices((prevPrices) => {
            const newPrices = new Map(prevPrices);
            newPrices.set(productId, indexedPrice);
            return newPrices;
          });
        }
        return newSet;
      });
    },
    [calculateIndexedAttributePrice],
  );

  /**
   * Current effective price for a contract product (huur, provisie, attribuut).
   *
   * Delegates to the shared helper so this page, de indexaties-overzichtspagina
   * and the backend all pick the same price row. Deze pagina had zijn eigen
   * sorteerregel op effectiveDate, waardoor de indexatiemail een ander bedrag
   * toonde dan YallyHub en Odoo — zie `deriveCurrentPrice` voor de details.
   */
  const getCurrentProductPrice = (product: any): number =>
    deriveCurrentPrice(product);

  const handleOpenEmail = () => {
    if (!contract) return;

    // Extract tenant contacts from contract with proper email path
    const tenantContacts =
      contract.contractContacts
        ?.filter((c: any) => c.contactType === ContactType.TENANT)
        .map((c: any) => ({
          id: c.resource?.id || "",
          name: c.resource?.name || "",
          resourceId: c.resource?.resourceId || "",
          email: c.relatedContact?.email || "",
          firstName: c.relatedContact?.firstName || "",
          lastName: c.relatedContact?.lastName || "",
          phoneMobile: c.relatedContact?.phoneMobile || "",
          units: [],
        })) || [];

    // Calculate provision and building attribute prices from contract products
    let totalProvision = 0;
    let totalBuildingAttributesOld = 0;
    let totalBuildingAttributesNew = 0;
    const buildingAttributeItems: Array<{
      name: string;
      oldPrice: number;
      newPrice: number;
      isIndexed: boolean;
      productId: number;
    }> = [];

    if (contract.products && contract.products.length > 0) {
      contract.products.forEach((product) => {
        const currentPrice = getCurrentProductPrice(product);

        // Check if this is a rental product (ledger 700001 or 700002) - these are indexed
        const isRentalProduct =
          product.template?.ledger === 700001 ||
          product.template?.ledger === 700002;

        if (!isRentalProduct) {
          // This is either a provision or building attribute
          if (product.buildingAttribute) {
            // Building attribute (parking, storage, etc.)
            // Only index if checked
            const isNonZero = currentPrice > 0;
            const isChecked = indexedAttributes.has(product.id);
            const newPrice =
              isNonZero && isChecked
                ? attributeIndexedPrices.get(product.id) ||
                  calculateIndexedAttributePrice(currentPrice)
                : currentPrice;

            totalBuildingAttributesOld += currentPrice;
            totalBuildingAttributesNew += newPrice;

            buildingAttributeItems.push({
              name: product.name,
              oldPrice: currentPrice,
              newPrice: newPrice,
              isIndexed: isNonZero && isChecked, // Show as indexed only if checked
              productId: product.id,
            });
          } else {
            // Provision
            totalProvision += currentPrice;
          }
        }
      });
    }

    // Get anniversary date for email
    const anniversaryDate = contract.contractMilestoneDates?.find(
      (m) => m.milestoneDateType === MilestoneDateType.START_DATE,
    )?.date;

    // Determine the CORRECT anniversary year for this indexation.
    // For on-time indexations this is the current year; for missed/late
    // indexations the backend selected last year's anniversary and returned it
    // as `result.yearSelected`. Falling back to the same logic the retroactive
    // useEffect uses keeps the wording consistent if the backend ever lags.
    const todayForEmail = new Date();
    const startDateObj = anniversaryDate ? new Date(anniversaryDate) : null;
    const lastIdxForEmail = contract.lastIndexationDate;
    const hasRecentIdxForEmail =
      lastIdxForEmail &&
      todayForEmail.getTime() - new Date(lastIdxForEmail).getTime() <
        365 * 24 * 60 * 60 * 1000;
    const correctAnniversaryYear =
      result?.yearSelected ??
      (startDateObj &&
      new Date(
        todayForEmail.getFullYear(),
        startDateObj.getMonth(),
        startDateObj.getDate(),
      ) > todayForEmail &&
      !hasRecentIdxForEmail
        ? todayForEmail.getFullYear() - 1
        : todayForEmail.getFullYear());

    const correctAnniversaryDate = startDateObj
      ? new Date(
          correctAnniversaryYear,
          startDateObj.getMonth(),
          startDateObj.getDate(),
        )
      : null;
    // The human template spells the date out ("11 december 2025") instead of
    // using 11/12/2025 — it reads less like a formal notice.
    const correctAnniversaryDateStr = correctAnniversaryDate
      ? correctAnniversaryDate.toLocaleDateString("nl-BE", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "";

    // Get sign date for the base date (used for the "old index" month/year)
    const signDate = contract.contractMilestoneDates?.find(
      (m) => m.milestoneDateType === MilestoneDateType.SIGN_DATE,
    )?.date;

    // Calculate total price components for email
    const finalIndexedRent =
      parseFloat(editableIndexedRent) || result?.result || 0;
    const totalPrice =
      finalIndexedRent + totalProvision + totalBuildingAttributesNew;

    // Building attributes (garage, berging, ...) as list items — the price block
    // is a bullet list so huurprijs/garage/provisie staan compact bij elkaar.
    const buildingAttributesText = buildingAttributeItems
      .map((item) => {
        const suffix = item.isIndexed
          ? `(geïndexeerd van ${applyMask(item.oldPrice, "valuta_specific")})`
          : "(ongewijzigd)";
        return `  <li><strong>${item.name}:</strong> ${applyMask(item.newPrice, "valuta_specific")} ${suffix}</li>`;
      })
      .join("\n");

    // Retroactive settlement — calculated up front because the wording of the
    // monthly-price block depends on whether there is a one-off amount due.
    const totalMonths = previousMonthsMultiplier + currentMonthMultiplier;
    // Use originalActivePrice if available (after apply), otherwise the contract's current price
    const oldActivePrice =
      originalActivePrice !== null
        ? originalActivePrice
        : contract.activePrice?.price || 0;
    const monthlyPriceDiff = finalIndexedRent - oldActivePrice;
    const totalCompensation = monthlyPriceDiff * totalMonths;
    const hasRetroactive = totalMonths > 0 && !!result && totalCompensation > 0;

    console.log("📧 [Email Generation] Retroactive section:", {
      previousMonthsMultiplier,
      currentMonthMultiplier,
      totalMonths,
      hasResult: !!result,
      oldActivePrice: oldActivePrice.toFixed(2),
      monthlyPriceDiff: monthlyPriceDiff.toFixed(2),
      totalCompensation: totalCompensation.toFixed(2),
      hasRetroactive,
    });

    // With a settlement the tenant starts paying the new price next month;
    // without one it simply starts on the indexation anniversary itself.
    const newPriceStartsFrom = hasRetroactive
      ? "volgende maand"
      : correctAnniversaryDateStr;

    // Doorlopende scheidingslijn. Quill stript <hr> en border-styling, dus de
    // lijn is opgebouwd uit U+2500 (box drawing light horizontal): die glyph
    // vult de volledige tekenbreedte en sluit dus naadloos aan, in tegenstelling
    // tot een rij koppelteken-streepjes.
    const divider = `<p>${"─".repeat(60)}</p>`;

    // Uitlijning van de noemer onder de teller: "Nieuwe huurprijs = " is ~19
    // harde spaties breed in de mailfonts.
    const formulaIndent = "&nbsp;".repeat(19);

    // Numerieke uitwerking van de formule + uitleg over de EPC-correctiecoëfficiënt.
    // Enkel wanneer er een berekening is; anders zouden er lege waarden in de
    // rekensom staan ("= × /"). De BE geeft coëfficiënt 1 terug voor labels
    // A+/A/B/C én voor D/E/F-contracten gestart op of na 1 oktober 2022.
    const epcLabelValue = contract.currentEPC?.label || "";
    const isDefLabel = ["D", "E", "F"].includes(epcLabelValue.toUpperCase());
    const hasCorrection = !!result && result.correctionCoefficient !== 1;
    const isPostOct2022NoCorrection =
      !!result && result.correctionCoefficient === 1 && isDefLabel;

    let correctionExplanation = "";
    if (hasCorrection) {
      correctionExplanation = `<p>Omdat je contract gestart is vóór 1 oktober 2022 en je woning EPC-label <strong>{epcLabel}</strong> heeft, wordt er volgens de Vlaamse regelgeving een correctiecoëfficiënt van <strong>{correctionCoefficient}</strong> toegepast.</p>`;
    } else if (isPostOct2022NoCorrection) {
      correctionExplanation = `<p>Omdat je contract gestart is na 1 oktober 2022, is er voor je woning (EPC-label <strong>{epcLabel}</strong>) geen correctiecoëfficiënt van toepassing.</p>`;
    } else if (result && epcLabelValue) {
      correctionExplanation = `<p>Je woning heeft EPC-label <strong>{epcLabel}</strong>, waardoor er geen correctiecoëfficiënt van toepassing is.</p>`;
    }

    const formulaInNumbers = result
      ? `
<p><strong>Dit geeft in cijfers:</strong></p>

<p>{newPrice} = ${
          hasCorrection
            ? "({basePrice} × {indexNew} / {indexOld}) × {correctionCoefficient}"
            : "{basePrice} × {indexNew} / {indexOld}"
        }</p>

<ul>
  <li><strong>Basishuurprijs:</strong> {basePrice}</li>
  <li><strong>Gezondheidsindex {indexNewMonth} {indexNewYear}:</strong> {indexNew}</li>
  <li><strong>Gezondheidsindex {indexOldMonth} {indexOldYear}:</strong> {indexOld}</li>${
    hasCorrection
      ? `\n  <li><strong>Correctiecoëfficiënt (EPC-label {epcLabel}):</strong> {correctionCoefficient}</li>`
      : ""
  }
</ul>
${correctionExplanation ? `\n${correctionExplanation}\n` : ""}`
      : "";

    // Indexation email — deliberately informal ("je"-vorm) so it reads like a
    // person wrote it instead of a legal notice. Values in {} are resolved by
    // the mass-mail placeholder engine (see contextPlaceholders below).
    let defaultEmailContent = `<p>Hi {contact.firstName},</p>

<p>Ik hoop dat alles goed met je gaat! 😊</p>

<p>Je ontvangt deze e-mail omdat het weer tijd is voor de <strong>jaarlijkse indexatie van je huurprijs</strong>.</p>

<p>We weten dat dit nooit leuk nieuws is om te krijgen. Daarom houden we het graag zo duidelijk en eenvoudig mogelijk. De indexatie is wettelijk voorzien en wordt voor alle huurders op dezelfde manier berekend aan de hand van de officiële gezondheidsindex.</p>

<p><strong><u>Je nieuwe huurprijs</u></strong></p>

<p>Vanaf <strong>${correctAnniversaryDateStr}</strong> wordt je huurprijs aangepast volgens de wettelijke formule:</p>

<p>Nieuwe huurprijs = <u>Basishuurprijs × Nieuwe gezondheidsindex</u><br/>
${formulaIndent}Gezondheidsindex bij de start van je huur</p>
${formulaInNumbers}
<p>Voor jouw woning betekent dit:</p>

<ul>
  <li><strong>Huurprijs:</strong> {newPrice}</li>${buildingAttributesText ? `\n${buildingAttributesText}` : ""}${totalProvision > 0 ? `\n  <li><strong>Provisie:</strong> {activeProvision} (ongewijzigd)</li>` : ""}
</ul>

<p><strong>Vanaf ${newPriceStartsFrom} betaal je in totaal {totalPrice} per maand</strong>.</p>

<p>Gelieve dit bedrag vanaf ${newPriceStartsFrom} vóór de <strong>1e dag van de maand</strong> over te schrijven op het vertrouwde rekeningnummer:</p>

<p><strong>{iban}</strong></p>
`;

    // One-off settlement for the months between the legal anniversary and today.
    // Bewust geen scheidingslijn tussen het IBAN en dit blok.
    if (hasRetroactive) {
      defaultEmailContent += `
<p><strong><u>Eenmalige verrekening:</u></strong></p>

<p>Omdat de indexatie volgens de wet al inging op ${correctAnniversaryDateStr}, maar nu pas wordt toegepast, moet het verschil van de voorbije ${totalMonths === 1 ? "maand" : "maanden"} nog één keer worden verrekend.</p>

<p><strong>Eenmalig bij te betalen:</strong> ${applyMask(totalCompensation, "valuta_specific")}</p>

<p>Vanaf volgende maand betaal je gewoon nog je nieuwe maandelijkse huurprijs van {totalPrice}.</p>
`;
    }

    defaultEmailContent += `
${divider}

<p><strong>Nog twee kleine vraagjes</strong> 😊</p>

<p><strong>1. Onderhoud gasketel</strong></p>

<p>Heeft je woning een gasketel? Dan ontvangen we graag het meest recente onderhoudsattest, indien we dit nog niet hebben.</p>

<p><strong>2. Brandverzekering</strong></p>

<p>Kan je ons ook even een geldig attest of betaalbewijs van je brandverzekering bezorgen? 😊 Ook als we je polis al in ons bezit hebben, zijn we verplicht om dit jaarlijks opnieuw op te vragen zodat ons dossier up-to-date blijft.</p>

<p>Beide documenten mag je gewoon beantwoorden op deze e-mail. Heb je deze attesten al doorgestuurd? Dan mag je deze herinnering uiteraard gewoon negeren.</p>

${divider}

<p>Heb je nog vragen over de indexatie of de berekening? Laat het gerust weten. We helpen je met plezier verder!</p>

<p>Alvast bedankt voor je medewerking en nog een fijne dag!</p>

<p>Vriendelijke groeten,</p>

<p><strong>Team Yally</strong></p>`;

    // Prepare index date strings for email.
    // For the NEW index we want the month/year that the backend actually used:
    // the indexation anniversary itself (or the preceding month, depending on
    // contract law era — but the BE returns result.yearSelected, and the
    // anniversary MONTH matches the contract start month). Using
    // correctAnniversaryDate keeps the year correct for late indexations.
    const indexNewDate = correctAnniversaryDate ?? new Date();
    const indexOldDate = signDate ? new Date(signDate) : new Date();

    // Get IBAN - prefer backend response (most up-to-date), fallback to contract data
    const finalIban =
      backendIban ||
      contract.unit?.building?.company?.ibanRentDeposit ||
      "IBAN niet beschikbaar";
    const companyName = contract.unit?.building?.company?.name || "Yally";

    // Open the mass-mail composer in a modal on this page (no new tab / navigation).
    setEmailModalState({
      contacts: tenantContacts,
      emailSubject: `Huurindexering ${contract.unit?.building?.googlePlaceLongAddress || contract.unit?.building?.id || ""}`,
      emailContent: defaultEmailContent,
      contextPlaceholders: {
        contractId: contract.id?.toString() || "",
        correctionCoefficient:
          result?.correctionCoefficient !== undefined
            ? formatCorrectionFactor(result.correctionCoefficient)
            : "",
        epcLabel: contract.currentEPC?.label || "",
        basePrice: applyMask(contract.initPrice || 0, "valuta_specific"),
        oldPrice: applyMask(
          originalActivePrice || contract.activePrice?.price || 0,
          "valuta_specific",
        ),
        newPrice: applyMask(finalIndexedRent, "valuta_specific"), // Use editable value (indexed rental price)
        activeProvision: applyMask(totalProvision, "valuta_specific"), // Provision price (not indexed)
        totalPrice: applyMask(totalPrice, "valuta_specific"), // Total = indexed rent + provision + attributes
        indexOld: result?.indexOld.toFixed(2) || "",
        indexNew: result?.index.toFixed(2) || "",
        indexOldMonth: indexOldDate.toLocaleDateString("nl-BE", {
          month: "long",
        }),
        indexOldYear: indexOldDate.getFullYear().toString(),
        indexNewMonth: indexNewDate.toLocaleDateString("nl-BE", {
          month: "long",
        }),
        indexNewYear: indexNewDate.getFullYear().toString(),
        buildingAddress:
          contract.unit?.building?.googlePlaceLongAddress ||
          "het gehuurde pand",
        companyName: companyName,
        iban: finalIban,
      },
      attachedFiles: [], // No PDF attachment
    });
  };

  const tenantNames =
    contract?.contractContacts
      ?.filter((c: any) => c.contactType === ContactType.TENANT)
      .map((c: any) => c.resource?.name || "")
      .filter(Boolean)
      .join(", ") || "Geen huurder";

  const anniversaryDate = contract?.contractMilestoneDates?.find(
    (m) => m.milestoneDateType === MilestoneDateType.START_DATE,
  )?.date;

  // Calculate the correct anniversary year based on last indexation date
  // If never indexed or last indexed > 1 year ago, use the missed anniversary year
  const getCorrectAnniversaryYear = (): number => {
    const now = new Date();
    if (!anniversaryDate) return now.getFullYear();

    const start = new Date(anniversaryDate);
    const lastIdx = contract?.lastIndexationDate;
    const hasRecentIndexation =
      lastIdx &&
      now.getTime() - new Date(lastIdx).getTime() < 365 * 24 * 60 * 60 * 1000;

    const currentYearAnniversary = new Date(
      now.getFullYear(),
      start.getMonth(),
      start.getDate(),
    );

    if (currentYearAnniversary > now && !hasRecentIndexation) {
      return now.getFullYear() - 1;
    }
    return now.getFullYear();
  };

  const correctAnniversaryYear = getCorrectAnniversaryYear();

  if (isLoadingContract) {
    return (
      <div
        className={`flex items-center justify-center ${embedded ? "h-64" : "h-screen"}`}
      >
        <div className="text-lg">Contract laden...</div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div
        className={`flex items-center justify-center ${embedded ? "h-64" : "h-screen"}`}
      >
        <div className="text-lg">Contract niet gevonden</div>
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? "bg-brick-secondary-fine"
          : "bg-brick-secondary-fine min-h-screen"
      }
    >
      {/* Header — sticky/full-bleed only on the standalone page, plain inside a modal */}
      <div
        className={
          embedded
            ? "mb-6 bg-white py-3 shadow-sm sm:py-6"
            : "sticky-below-header bleed-full-width mb-6 bg-white py-3 shadow-sm sm:py-6"
        }
      >
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          {/* Mobile Layout */}
          <div className="block lg:hidden">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  isIconOnly
                  variant="light"
                  onPress={close}
                  className="text-neutral-secondary-mid hover:text-brick-primary"
                  size="sm"
                >
                  <ArrowLeft size={18} />
                </Button>
                <div>
                  <div className="text-base font-semibold">
                    IND{contract?.id}
                  </div>
                  <div className="text-neutral-secondary-mid text-xs">
                    {contract?.unit?.building?.id} - {contract?.unit?.id}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons - Mobile (moved to result section) */}

            {/* Chips - Mobile */}
            <div className="flex flex-wrap items-center gap-1">
              {contract?.unit?.building && (
                <Chip
                  size="sm"
                  className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 border text-xs"
                  startContent={<Buildings size={10} />}
                >
                  {contract.unit.building.id}
                </Chip>
              )}
              {tenantNames !== "Geen huurder" && (
                <Chip
                  size="sm"
                  className="bg-brick-primary/10 text-brick-primary border-brick-primary/30 border text-xs"
                  startContent={<UserCircle size={10} />}
                >
                  {tenantNames}
                </Chip>
              )}
              {anniversaryDate && (
                <Chip
                  size="sm"
                  className="bg-neutral-primary/10 text-neutral-primary border-neutral-primary/30 border text-xs"
                  startContent={<Calendar size={10} />}
                >
                  {formatDate(anniversaryDate, "dd-MM")}
                </Chip>
              )}
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:block">
            <div className="flex items-start justify-between">
              {/* Left side - Back button and title */}
              <div className="flex flex-1 items-start gap-4">
                <Button
                  isIconOnly
                  variant="light"
                  onPress={close}
                  className="text-neutral-secondary-mid hover:text-brick-primary mt-1"
                >
                  <ArrowLeft size={20} />
                </Button>
                <div className="flex-1">
                  {/* Title section */}
                  <div className="mb-3 flex items-center gap-3">
                    <div className="bg-brick-primary h-8 w-3 rounded-full"></div>
                    <div className="text-neutral-primary text-2xl font-bold">
                      IND{contract?.id}
                    </div>
                    <h1 className="text-neutral-primary text-lg font-semibold">
                      Indexatie Berekening
                    </h1>
                  </div>

                  {/* Metadata chips - Desktop */}
                  <div className="flex flex-wrap items-center gap-2">
                    {contract?.unit?.building && (
                      <Chip
                        size="sm"
                        className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 border"
                        startContent={<Buildings size={12} />}
                      >
                        {contract.unit.building.id}
                      </Chip>
                    )}
                    {contract?.unit && (
                      <Chip
                        size="sm"
                        className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 border"
                      >
                        Unit {contract.unit.id}
                      </Chip>
                    )}
                    {tenantNames !== "Geen huurder" && (
                      <Chip
                        size="sm"
                        className="bg-brick-primary/10 text-brick-primary border-brick-primary/30 border"
                        startContent={<UserCircle size={12} />}
                      >
                        {tenantNames}
                      </Chip>
                    )}
                    {anniversaryDate && (
                      <Chip
                        size="sm"
                        className="bg-neutral-primary/10 text-neutral-primary border-neutral-primary/30 border"
                        startContent={<Calendar size={12} />}
                      >
                        Verjaardag contract:{" "}
                        {formatDate(anniversaryDate, "dd-MM")}/
                        {correctAnniversaryYear}
                      </Chip>
                    )}
                  </div>
                </div>
              </div>

              {/* Right side - Empty for now, actions moved to result card */}
              <div className="flex items-center gap-3">
                {/* Action buttons moved to result section */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        {isCalculating ? (
          // Loading state while calculating
          <Card>
            <CardBody className="flex flex-col items-center justify-center gap-4 py-12">
              <Calculator
                weight="duotone"
                size={48}
                className="text-moss-primary animate-pulse"
              />
              <div className="text-lg font-semibold">
                Berekening wordt uitgevoerd...
              </div>
              <div className="text-neutral-secondary-mid text-sm">
                Even geduld, de indexatie wordt berekend
              </div>
            </CardBody>
          </Card>
        ) : calculationError ? (
          // Error state
          <Card>
            <CardBody className="flex flex-col items-center justify-center gap-4 py-12">
              <Calculator
                weight="duotone"
                size={48}
                className="text-brick-primary"
              />
              <div className="text-brick-primary text-lg font-semibold">
                Berekening mislukt
              </div>
              <div className="text-neutral-secondary-mid max-w-md text-center text-sm">
                {calculationError}
              </div>
              <Button variant="flat" onPress={close}>
                Terug naar overzicht
              </Button>
            </CardBody>
          </Card>
        ) : result ? (
          // Result View
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendUp
                  weight="duotone"
                  size={24}
                  className="text-moss-primary"
                />
                <span className="text-lg font-semibold">
                  Indexatie Resultaat
                </span>
              </div>
            </CardHeader>
            <CardBody className="flex flex-col gap-6">
              {/* Date Information - Anniversary and Months Elapsed */}
              {anniversaryDate &&
                (() => {
                  const anniversary = new Date(anniversaryDate);
                  const today = new Date();

                  // Build the correct anniversary date with the right year
                  const correctAnniversary = new Date(
                    correctAnniversaryYear,
                    anniversary.getMonth(),
                    anniversary.getDate(),
                  );

                  // Calculate months elapsed since the correct anniversary
                  let monthsDiff =
                    (today.getFullYear() - correctAnniversary.getFullYear()) *
                      12 +
                    (today.getMonth() - correctAnniversary.getMonth());
                  if (monthsDiff < 0) monthsDiff = 0;

                  const anniversaryStr =
                    formatDate(anniversaryDate as Date, "dd-MM") +
                    "/" +
                    correctAnniversaryYear;
                  const todayStr = today.toLocaleDateString("nl-BE", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  });

                  // Last indexation display
                  const lastIdxDate = contract?.lastIndexationDate;
                  const lastIdxStr = lastIdxDate
                    ? new Date(lastIdxDate).toLocaleDateString("nl-BE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : "Onbekend";

                  return (
                    <Card className="border-2 border-purple-200 bg-purple-50/30">
                      <CardBody className="space-y-3">
                        <div className="grid grid-cols-1 gap-4 text-center md:grid-cols-5">
                          <div className="rounded-lg border border-purple-200 bg-white p-4">
                            <div className="mb-2 text-xs font-medium text-purple-700">
                              Indexatieverjaardag
                            </div>
                            <div className="text-2xl font-bold text-purple-900">
                              {anniversaryStr}
                            </div>
                          </div>
                          <div className="rounded-lg border border-purple-200 bg-white p-4">
                            <div className="mb-2 text-xs font-medium text-purple-700">
                              Laatste indexatie
                            </div>
                            <div
                              className={`text-2xl font-bold ${lastIdxDate ? "text-purple-900" : "text-red-600"}`}
                            >
                              {lastIdxStr}
                            </div>
                          </div>
                          <div className="rounded-lg border border-purple-200 bg-white p-4">
                            <div className="mb-2 text-xs font-medium text-purple-700">
                              Vandaag
                            </div>
                            <div className="text-2xl font-bold text-purple-900">
                              {todayStr}
                            </div>
                          </div>
                          <div className="rounded-lg border border-purple-200 bg-white p-4">
                            <div className="mb-2 text-xs font-medium text-purple-700">
                              Maanden verstreken
                            </div>
                            <div className="text-2xl font-bold text-purple-900">
                              {monthsDiff}
                            </div>
                          </div>
                          <div className="rounded-lg border border-purple-200 bg-white p-4">
                            <div className="mb-2 text-xs font-medium text-purple-700">
                              Maanden retroactief te factureren
                            </div>
                            <div className="text-2xl font-bold text-purple-900">
                              {Math.min(monthsDiff + 1, 4)}
                            </div>
                          </div>
                        </div>
                        <div className="text-center text-sm text-purple-800">
                          <strong>{anniversaryStr}</strong> was de
                          indexatieverjaardag. We zijn vandaag{" "}
                          <strong>{todayStr}</strong>. Er{" "}
                          {monthsDiff === 1 ? "is" : "zijn"}{" "}
                          <strong>
                            {monthsDiff}{" "}
                            {monthsDiff === 1 ? "maand" : "maanden"}
                          </strong>{" "}
                          verstreken sinds de verjaardag.
                          <br />
                          <strong>
                            {Math.min(monthsDiff + 1, 4)}{" "}
                            {Math.min(monthsDiff + 1, 4) === 1
                              ? "maand"
                              : "maanden"}
                          </strong>{" "}
                          te factureren (huidige maand + max. 3 vorige maanden).
                        </div>
                      </CardBody>
                    </Card>
                  );
                })()}

              {/* Foutmelding: basishuurprijs hoger dan huidige huurprijs */}
              {(() => {
                const basePrice = contract.initPrice ?? 0;
                const currentPrice =
                  originalActivePrice ?? contract.activePrice?.price ?? 0;
                if (
                  basePrice > 0 &&
                  currentPrice > 0 &&
                  basePrice > currentPrice
                ) {
                  return (
                    <div className="flex items-start gap-3 rounded-lg border-2 border-red-300 bg-red-50 p-4">
                      <XCircle
                        weight="fill"
                        size={22}
                        className="mt-0.5 shrink-0 text-red-600"
                      />
                      <div>
                        <div className="font-semibold text-red-800">
                          Basishuurprijs hoger dan huidige huurprijs
                        </div>
                        <div className="mt-1 text-sm text-red-700">
                          De basishuurprijs (€&nbsp;
                          {basePrice.toFixed(2).replace(".", ",")}) is hoger dan
                          de huidige actieve huurprijs (€&nbsp;
                          {currentPrice.toFixed(2).replace(".", ",")}). Dit is
                          normaal gezien niet mogelijk. Controleer de
                          contractgegevens voordat u indexeert.
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Waarschuwing: stijging groter dan 10 % */}
              {(() => {
                if (!result) return null;
                const currentPrice =
                  originalActivePrice ?? contract.activePrice?.price ?? 0;
                if (currentPrice <= 0) return null;
                const increase = (result.result - currentPrice) / currentPrice;
                if (increase > 0.1) {
                  return (
                    <div className="flex items-start gap-3 rounded-lg border-2 border-red-400 bg-red-50 p-4">
                      <Warning
                        weight="fill"
                        size={22}
                        className="mt-0.5 shrink-0 text-red-600"
                      />
                      <div>
                        <div className="font-semibold text-red-800">
                          Stijging groter dan 10 % — controleer kritisch
                        </div>
                        <div className="mt-1 text-sm text-red-700">
                          De berekende nieuwe huurprijs (€&nbsp;
                          {result.result.toFixed(2).replace(".", ",")}) is&nbsp;
                          <strong>
                            {(increase * 100).toFixed(1).replace(".", ",")}%
                          </strong>{" "}
                          hoger dan de huidige huurprijs (€&nbsp;
                          {currentPrice.toFixed(2).replace(".", ",")}).
                          Controleer de berekening grondig voordat u bevestigt.
                        </div>
                        {contract.unit?.building?.id && (
                          <div className="mt-2 text-sm text-red-700">
                            Meestal heeft dit te maken met het feit dat de
                            basishuurprijs origineel niet goed is ingegeven. U
                            kan deze bewerken onder het tabje{" "}
                            <a
                              href={`/building/${contract.unit.building.id}/verhuur`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold underline hover:text-red-900"
                            >
                              Verhuur
                            </a>{" "}
                            van dit gebouw.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Waarschuwing: nieuwe huurprijs lager dan huidige huurprijs */}
              {(() => {
                if (!result) return null;
                const currentPrice =
                  originalActivePrice ?? contract.activePrice?.price ?? 0;
                if (currentPrice <= 0) return null;
                const change = (result.result - currentPrice) / currentPrice;
                if (change >= 0) return null;
                const decreasePct = Math.abs(change) * 100;
                return (
                  <div className="flex items-start gap-3 rounded-lg border-2 border-red-400 bg-red-50 p-4">
                    <Warning
                      weight="fill"
                      size={22}
                      className="mt-0.5 shrink-0 text-red-600"
                    />
                    <div>
                      <div className="font-semibold text-red-800">
                        Nieuwe huurprijs lager dan huidige huurprijs —
                        controleer kritisch
                      </div>
                      <div className="mt-1 text-sm text-red-700">
                        De berekende nieuwe huurprijs (€&nbsp;
                        {result.result.toFixed(2).replace(".", ",")}) is&nbsp;
                        <strong>
                          {decreasePct.toFixed(1).replace(".", ",")}%
                        </strong>{" "}
                        lager dan de huidige huurprijs (€&nbsp;
                        {currentPrice.toFixed(2).replace(".", ",")}). Een
                        indexatie zou normaal gezien tot een gelijke of hogere
                        huurprijs moeten leiden. Controleer de basishuurprijs en
                        de laatste indexatiedatum voordat u bevestigt.
                      </div>
                      {contract.unit?.building?.id && (
                        <div className="mt-2 text-sm text-red-700">
                          U kan de basishuurprijs bewerken onder het tabje{" "}
                          <a
                            href={`/building/${contract.unit.building.id}/verhuur`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold underline hover:text-red-900"
                          >
                            Verhuur
                          </a>{" "}
                          van dit gebouw.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Side-by-Side Price Comparison Tables */}
              {(() => {
                // Separate products into categories
                const rentalProducts: any[] = [];
                const provisionProducts: any[] = [];
                const attributeProducts: any[] = [];

                contract.products?.forEach((product) => {
                  const isRental =
                    product.template?.ledger === 700001 ||
                    product.template?.ledger === 700002;
                  if (isRental) {
                    rentalProducts.push(product);
                  } else if (product.buildingAttribute) {
                    attributeProducts.push(product);
                  } else {
                    provisionProducts.push(product);
                  }
                });

                // Calculate totals
                const oldRentalPrice =
                  (originalActivePrice ?? contract.activePrice?.price) || 0;
                const oldProvisionTotal = provisionProducts.reduce(
                  (sum, p) => sum + getCurrentProductPrice(p),
                  0,
                );
                const oldAttributesTotal = attributeProducts.reduce(
                  (sum, p) => sum + getCurrentProductPrice(p),
                  0,
                );
                const oldTotal =
                  oldRentalPrice + oldProvisionTotal + oldAttributesTotal;

                const newRentalPrice = parseFloat(editableIndexedRent) || 0;
                const newProvisionTotal = oldProvisionTotal; // Provisions don't change
                // Attributes: Only sum checked attributes
                const newAttributesTotal = attributeProducts.reduce(
                  (sum, p) => {
                    const currentPrice = getCurrentProductPrice(p);
                    if (currentPrice > 0 && indexedAttributes.has(p.id)) {
                      // Only include checked attributes
                      const indexedPrice =
                        attributeIndexedPrices.get(p.id) ||
                        calculateIndexedAttributePrice(currentPrice);
                      return sum + indexedPrice;
                    } else if (
                      currentPrice > 0 &&
                      !indexedAttributes.has(p.id)
                    ) {
                      // Unchecked attributes: use original price
                      return sum + currentPrice;
                    }
                    return sum;
                  },
                  0,
                );
                const newTotal =
                  newRentalPrice + newProvisionTotal + newAttributesTotal;

                return (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {/* LEFT TABLE: OLD PRICES */}
                    <Card className="border-2 border-orange-200 bg-orange-50/20">
                      <CardHeader className="border-b-2 border-orange-200 bg-orange-100/50">
                        <div className="flex items-center gap-2">
                          <div className="text-lg font-semibold text-orange-900">
                            HUIDIGE PRIJZEN
                          </div>
                        </div>
                      </CardHeader>
                      <CardBody className="space-y-3">
                        {/* Rental Price */}
                        <div className="flex items-center justify-between border-b border-orange-200/50 py-2">
                          <span className="text-sm font-medium">
                            Huidige huurprijs
                          </span>
                          <span className="text-lg font-semibold">
                            {applyMask(oldRentalPrice, "valuta_specific")}
                          </span>
                        </div>

                        {/* Attributes (BEFORE provision) */}
                        {attributeProducts.map((product) => (
                          <div
                            key={product.id}
                            className="flex items-center justify-between border-b border-orange-200/50 py-2"
                          >
                            <span className="text-sm font-medium">
                              {product.name}
                            </span>
                            <span className="text-sm">
                              {applyMask(
                                getCurrentProductPrice(product),
                                "valuta_specific",
                              )}
                            </span>
                          </div>
                        ))}

                        {/* Provision (AFTER attributes) */}
                        {provisionProducts.map((product) => (
                          <div
                            key={product.id}
                            className="flex items-center justify-between border-b border-orange-200/50 py-2"
                          >
                            <span className="text-sm font-medium">
                              {product.name}
                            </span>
                            <span className="text-sm">
                              {applyMask(
                                getCurrentProductPrice(product),
                                "valuta_specific",
                              )}
                            </span>
                          </div>
                        ))}

                        {/* Total */}
                        <div className="mt-2 flex items-center justify-between rounded-lg border-t-2 border-orange-300 bg-orange-100/50 px-3 py-3">
                          <span className="text-base font-bold text-orange-900">
                            TOTAAL
                          </span>
                          <span className="text-xl font-bold text-orange-900">
                            {applyMask(oldTotal, "valuta_specific")}
                          </span>
                        </div>
                      </CardBody>
                    </Card>

                    {/* RIGHT TABLE: NEW PRICES */}
                    <Card className="border-moss-primary/30 bg-moss-secondaryFine/30 border-2">
                      <CardHeader className="bg-moss-secondaryFine border-moss-primary/30 border-b-2">
                        <div className="flex items-center gap-2">
                          <div className="text-moss-primary text-lg font-semibold">
                            NIEUWE PRIJZEN (NA INDEXATIE)
                          </div>
                        </div>
                      </CardHeader>
                      <CardBody className="space-y-3">
                        {/* Rental Price (Editable) */}
                        <div className="border-moss-primary/20 flex items-center justify-between border-b py-2">
                          <span className="text-sm font-medium">
                            Geïndexeerde huurprijs
                          </span>
                          <Input
                            value={`€ ${editableIndexedRent}`}
                            onValueChange={(value) => {
                              const cleanValue = value
                                .replace(/[€\s]/g, "")
                                .replace(",", ".");
                              setEditableIndexedRent(cleanValue);
                            }}
                            classNames={{
                              base: "w-40",
                              input:
                                "text-lg font-semibold text-moss-primary text-right",
                              inputWrapper:
                                "border-2 border-moss-primary/30 hover:border-moss-primary",
                            }}
                            size="sm"
                          />
                        </div>

                        {/* Attributes (BEFORE provision - With checkbox to select/deselect indexation) */}
                        {attributeProducts.map((product) => {
                          const currentPrice = getCurrentProductPrice(product);
                          const isNonZero = currentPrice > 0;
                          const isIndexed = indexedAttributes.has(product.id);
                          const displayPrice = isIndexed
                            ? attributeIndexedPrices.get(product.id) ||
                              calculateIndexedAttributePrice(currentPrice)
                            : currentPrice;

                          return (
                            <div
                              key={product.id}
                              className="border-moss-primary/20 flex items-center justify-between border-b py-2"
                            >
                              <div className="flex items-center gap-2">
                                {isNonZero && (
                                  <input
                                    type="checkbox"
                                    checked={isIndexed}
                                    onChange={() =>
                                      toggleAttributeIndexation(
                                        product.id,
                                        currentPrice,
                                      )
                                    }
                                    className="text-moss-primary focus:ring-moss-primary rounded-sm border-gray-300"
                                  />
                                )}
                                <span className="text-sm font-medium">
                                  {product.name}
                                </span>
                                {isNonZero ? (
                                  isIndexed ? (
                                    <span className="text-moss-primary text-xs font-medium italic">
                                      (geïndexeerd)
                                    </span>
                                  ) : (
                                    <span className="text-neutral-secondary-mid text-xs italic">
                                      (ongewijzigd)
                                    </span>
                                  )
                                ) : (
                                  <span className="text-neutral-secondary-mid text-xs italic">
                                    (€ 0,00)
                                  </span>
                                )}
                              </div>
                              <span
                                className={`text-sm ${isIndexed && isNonZero ? "text-moss-primary font-semibold" : ""}`}
                              >
                                {applyMask(displayPrice, "valuta_specific")}
                              </span>
                            </div>
                          );
                        })}

                        {/* Provision (AFTER attributes - Unchanged) */}
                        {provisionProducts.map((product) => (
                          <div
                            key={product.id}
                            className="border-moss-primary/20 flex items-center justify-between border-b py-2"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                {product.name}
                              </span>
                              <span className="text-neutral-secondary-mid text-xs italic">
                                (ongewijzigd)
                              </span>
                            </div>
                            <span className="text-sm">
                              {applyMask(
                                getCurrentProductPrice(product),
                                "valuta_specific",
                              )}
                            </span>
                          </div>
                        ))}

                        {/* Total */}
                        <div className="border-moss-primary bg-moss-secondaryFine mt-2 flex items-center justify-between rounded-lg border-t-2 px-3 py-3">
                          <span className="text-moss-primary text-base font-bold">
                            TOTAAL
                          </span>
                          <span className="text-moss-primary text-xl font-bold">
                            {applyMask(newTotal, "valuta_specific")}
                          </span>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                );
              })()}

              {/* Detailed Calculation Formula */}
              {(() => {
                const signDate = contract.contractMilestoneDates?.find(
                  (m) => m.milestoneDateType === MilestoneDateType.SIGN_DATE,
                )?.date;
                const startDate = contract.contractMilestoneDates?.find(
                  (m) => m.milestoneDateType === MilestoneDateType.START_DATE,
                )?.date;

                if (!signDate || !startDate) return null;

                const signDateObj = new Date(signDate);
                const startDateObj = new Date(startDate);
                const currentDate = new Date();

                // Format dates for display
                const signDateMonth = signDateObj.toLocaleDateString("nl-BE", {
                  month: "long",
                });
                const signDateYear = signDateObj.getFullYear();
                const startDateMonth = startDateObj.toLocaleDateString(
                  "nl-BE",
                  { month: "long" },
                );
                const currentMonth = currentDate.toLocaleDateString("nl-BE", {
                  month: "long",
                });
                const currentYear = currentDate.getFullYear();

                // Calculate one year after start date for correction coefficient reference
                const oneYearAfterStart = new Date(startDateObj);
                oneYearAfterStart.setFullYear(
                  oneYearAfterStart.getFullYear() + 1,
                );
                const correctionYearMonth =
                  oneYearAfterStart.toLocaleDateString("nl-BE", {
                    month: "long",
                  });
                const correctionYear = oneYearAfterStart.getFullYear();

                const twoYearsAfterStart = new Date(startDateObj);
                twoYearsAfterStart.setFullYear(
                  twoYearsAfterStart.getFullYear() + 2,
                );
                const correctionYear2Month =
                  twoYearsAfterStart.toLocaleDateString("nl-BE", {
                    month: "long",
                  });
                const correctionYear2 = twoYearsAfterStart.getFullYear();

                const basePrice = contract.initPrice || 0;
                const newPrice = parseFloat(editableIndexedRent) || 0;

                return (
                  <Card className="border-2 border-blue-200 bg-blue-50/30">
                    <CardHeader className="border-b border-blue-200 bg-blue-100/50">
                      <div className="flex items-center gap-2">
                        <Calculator
                          weight="duotone"
                          size={20}
                          className="text-blue-600"
                        />
                        <span className="text-base font-semibold text-blue-900">
                          Toepassing Wettelijke Formule
                        </span>
                      </div>
                    </CardHeader>
                    <CardBody className="space-y-4">
                      {/* Formula with dates */}
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-blue-900">
                          Formule:
                        </div>
                        <div className="rounded-lg border border-blue-200 bg-white p-4 font-mono text-sm">
                          <div className="text-blue-900">
                            Nieuwe huurprijs ({currentMonth} {currentYear}) =
                          </div>
                          <div className="mt-2 ml-4 text-blue-800">
                            (Basishuurprijs ({signDateMonth} {signDateYear}) ×
                            GI [2013] {currentMonth} {currentYear} / GI [2013]{" "}
                            {signDateMonth} {signDateYear})
                          </div>
                          <div className="ml-4 text-blue-800">
                            × Correctiecoëfficiënt (GI [2013]{" "}
                            {correctionYearMonth} {correctionYear} / GI [2013]{" "}
                            {correctionYear2Month} {correctionYear2})
                          </div>
                        </div>
                      </div>

                      {/* Numerical calculation */}
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-blue-900">
                          Dit geeft in cijfers:
                        </div>
                        <div className="rounded-lg border border-blue-200 bg-white p-4">
                          <div className="font-mono text-base">
                            <span className="text-moss-primary font-bold">
                              {applyMask(newPrice, "valuta_specific")}
                            </span>
                            {" = "}
                            <span className="text-blue-800">
                              ({applyMask(basePrice, "valuta_specific")} ×{" "}
                              {result.index.toFixed(2)} /{" "}
                              {result.indexOld.toFixed(2)})
                            </span>
                            {" × "}
                            <span className="text-blue-800">
                              {formatCorrectionFactor(
                                result.correctionCoefficient,
                              )}
                            </span>
                          </div>
                          <div className="text-neutral-secondary-mid mt-3 text-xs">
                            Deze nieuwe huurprijs is geldig vanaf{" "}
                            {startDateMonth} {currentYear}
                          </div>
                        </div>
                      </div>

                      {/* EPC Label Explanation */}
                      {contract.currentEPC?.label &&
                        (() => {
                          const isDefLabel = ["D", "E", "F"].includes(
                            contract.currentEPC!.label.toUpperCase(),
                          );
                          const isNoCorrection =
                            result.correctionCoefficient === 1 && isDefLabel;
                          return isNoCorrection ? (
                            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                              <div className="text-sm text-green-800">
                                <strong>
                                  EPC Label {contract.currentEPC!.label}:
                                </strong>{" "}
                                Geen correctiecoëfficiënt van toepassing.
                                Contracten gestart vanaf 1 oktober 2022 worden
                                volledig geïndexeerd zonder correctiefactor.
                              </div>
                            </div>
                          ) : (
                            <div className="bg-moss-secondaryFine/30 border-moss-primary/20 rounded-lg border p-3">
                              <div className="text-moss-primary text-sm">
                                <strong>
                                  EPC Label {contract.currentEPC!.label}:
                                </strong>{" "}
                                De correctiecoëfficiënt van{" "}
                                <strong>
                                  {formatCorrectionFactor(
                                    result.correctionCoefficient,
                                  )}
                                </strong>{" "}
                                wordt toegepast volgens de Vlaamse regelgeving.
                              </div>
                            </div>
                          );
                        })()}
                    </CardBody>
                  </Card>
                );
              })()}

              {/* Retroactive Invoice Section */}
              {!isApplied && retroactiveAmount > 0 && (
                <Card className="border-2 border-orange-200 bg-orange-50/50">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Buildings
                        weight="duotone"
                        size={20}
                        className="text-orange-600"
                      />
                      <span className="text-base font-semibold text-orange-900">
                        Retroactief Bedrag
                      </span>
                    </div>
                  </CardHeader>
                  <CardBody className="space-y-4">
                    {/* Explanation */}
                    <p className="text-sm text-orange-800">
                      Sinds de verjaardag van het contract{" "}
                      {anniversaryDate
                        ? "(" +
                          formatDate(anniversaryDate as Date, "dd-MM") +
                          "/" +
                          correctAnniversaryYear +
                          ")"
                        : ""}{" "}
                      kunt u een factuur aanmaken voor het retroactieve bedrag.
                    </p>

                    {/* Calculation Display - Split previous + current months */}
                    <div className="rounded-lg border border-orange-200 bg-white p-4">
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <div className="mb-1 text-xs text-gray-600">
                            Prijsverschil
                          </div>
                          <div className="text-lg font-semibold text-gray-900">
                            {applyMask(
                              (parseFloat(editableIndexedRent) || 0) -
                                ((originalActivePrice ??
                                  contract.activePrice?.price) ||
                                  0),
                              "valuta_specific",
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="mb-1 text-xs text-gray-600">
                            × Vorige maanden
                          </div>
                          <div className="text-lg font-semibold text-gray-900">
                            {previousMonthsMultiplier}
                          </div>
                        </div>
                        <div>
                          <div className="mb-1 text-xs text-gray-600">
                            + Huidige maand
                          </div>
                          <div className="text-lg font-semibold text-gray-900">
                            {currentMonthMultiplier}
                          </div>
                        </div>
                        <div>
                          <div className="mb-1 text-xs text-gray-600">
                            = Compensatie
                          </div>
                          <div className="text-xl font-bold text-orange-600">
                            {applyMask(
                              ((parseFloat(editableIndexedRent) || 0) -
                                ((originalActivePrice ??
                                  contract.activePrice?.price) ||
                                  0)) *
                                (previousMonthsMultiplier +
                                  currentMonthMultiplier),
                              "valuta_specific",
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Previous months control only - current month is automatic */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Vorige maanden corrigeren (max 3)
                      </label>
                      <Input
                        type="number"
                        value={previousMonthsMultiplier.toString()}
                        onChange={(e) => {
                          const val = Math.max(
                            0,
                            Math.min(3, parseInt(e.target.value) || 0),
                          );
                          setpreviousMonthsMultiplier(val);

                          // Recalculate retroactive amount
                          const priceDiff =
                            (parseFloat(editableIndexedRent) || 0) -
                            ((originalActivePrice ??
                              contract.activePrice?.price) ||
                              0);
                          const newCompensation =
                            priceDiff * (val + currentMonthMultiplier);
                          setRetroactiveAmount(Math.max(0, newCompensation));

                          // Auto-disable invoice if total amount becomes 0
                          if (val + currentMonthMultiplier === 0)
                            setCreateInvoice(false);
                        }}
                        min={0}
                        max={3}
                        size="sm"
                        description="Alleen vorige maanden (afgeronde). Huidige maand (reeds begonnen) wordt automatisch toegevoegd."
                      />
                    </div>

                    {/* Total months info */}
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                      <div className="text-sm text-blue-800">
                        <strong>Totaal:</strong>{" "}
                        {previousMonthsMultiplier + currentMonthMultiplier}{" "}
                        maanden{" "}
                        {previousMonthsMultiplier > 0 &&
                          currentMonthMultiplier > 0 && (
                            <span>
                              ({previousMonthsMultiplier} vorige +{" "}
                              {currentMonthMultiplier} huidige)
                            </span>
                          )}
                      </div>
                    </div>

                    {/* Checkbox to enable invoice creation */}
                    <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-white p-3">
                      <input
                        type="checkbox"
                        checked={createInvoice}
                        onChange={(e) => setCreateInvoice(e.target.checked)}
                        disabled={
                          previousMonthsMultiplier + currentMonthMultiplier ===
                          0
                        }
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Maak factuur aan in Odoo
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          Bij het toepassen wordt automatisch een factuur
                          aangemaakt voor het retroactieve bedrag.
                        </div>
                      </div>
                    </div>

                    {/* Preview of invoice creation */}
                    {createInvoice &&
                      previousMonthsMultiplier + currentMonthMultiplier > 0 && (
                        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                          <div className="text-sm text-green-800">
                            <strong>✓</strong> Er wordt een factuur aangemaakt
                            van{" "}
                            <strong>
                              {applyMask(
                                ((parseFloat(editableIndexedRent) || 0) -
                                  ((originalActivePrice ??
                                    contract.activePrice?.price) ||
                                    0)) *
                                  (previousMonthsMultiplier +
                                    currentMonthMultiplier),
                                "valuta_specific",
                              )}
                            </strong>{" "}
                            voor{" "}
                            <strong>
                              {previousMonthsMultiplier +
                                currentMonthMultiplier}{" "}
                              maanden
                            </strong>{" "}
                            retroactieve indexatie.
                          </div>
                        </div>
                      )}
                  </CardBody>
                </Card>
              )}

              {/* Invoice Created Success Message */}
              {isApplied && invoiceUrl && (
                <Card className="border-2 border-green-200 bg-green-50/50">
                  <CardBody className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle
                        weight="fill"
                        size={24}
                        className="mt-0.5 text-green-600"
                      />
                      <div className="flex-1">
                        <div className="mb-1 text-base font-semibold text-green-900">
                          Factuur succesvol aangemaakt!
                        </div>
                        <p className="mb-3 text-sm text-green-800">
                          De retroactieve factuur werd aangemaakt in Odoo. De
                          factuur link zal worden toegevoegd aan de
                          indexatiemail.
                        </p>
                        <a
                          href={invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                        >
                          <Buildings size={16} />
                          Bekijk factuur in Odoo
                        </a>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between border-t pt-4">
                <div className="flex gap-3">
                  <Button
                    variant="flat"
                    startContent={<Globe weight="duotone" size={16} />}
                    onPress={handleOpenOfficialTool}
                  >
                    Verifieer met Statbel
                  </Button>
                  {isApplied && (
                    <ButtonNew
                      variant="primaryMoss"
                      onClick={handleOpenEmail}
                      icon={<Envelope />}
                      label="Verstuur Indexatiemail"
                    />
                  )}
                </div>
                <div className="flex gap-3">
                  <Button variant="light" onPress={close}>
                    Sluiten
                  </Button>
                  <Tooltip
                    content={
                      <div className="max-w-xs px-1 py-2">
                        <div className="text-xs font-semibold">
                          Toepassen indexatie:
                        </div>
                        <div className="mt-1 text-xs">
                          • Past actieve prijs aan in YallyHub
                          <br />
                          • Pusht nieuwe prijs naar Odoo
                          <br />
                          • Bij eerstvolgende facturatie wordt geïndexeerde
                          prijs gebruikt
                          <br />• Nadien kunt u indexatiemail versturen
                        </div>
                      </div>
                    }
                    placement="top"
                  >
                    <Button
                      color="primary"
                      onPress={handleApply}
                      isLoading={isApplying}
                      isDisabled={isApplying || isApplied}
                    >
                      {isApplied ? "Toegepast ✓" : "Toepassen"}
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </CardBody>
          </Card>
        ) : null}
      </div>

      {/* Indexatiemail composer — opens in a popup on this page instead of a
          new tab, so you can keep working after sending. */}
      <MassMailModal
        isOpen={!!emailModalState}
        onClose={() => setEmailModalState(null)}
        initialState={emailModalState ?? undefined}
        onSuccess={() => setEmailModalState(null)}
      />
    </div>
  );
};
